import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import { offersAPI } from '../../lib/api';
import { getScoreColor } from '../../lib/scoreUtils';

export default function CandidateIntelligenceTab({ candidate, candidateId, parsed, match, scoreBreakdown, matchedSkillsSet, depthAnalysis, analyzingDepth, depthError, setDepthError, runDepthAnalysis, offerProb, pipelineHistory, offer, showOfferForm, setShowOfferForm, offerForm, setOfferForm, onboardingPlan, setOnboardingPlan, loadCandidate }) {
  return (
        <TabsContent value="intelligence">
          {!depthAnalysis ? (
            <div>
              {analyzingDepth ? (
                <Card style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 14, padding: '2rem', textAlign: 'center' }}>
                  <span className="status-dot status-processing" style={{ display: 'inline-block', width: 12, height: 12, marginBottom: '0.75rem' }} />
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#92400E', marginBottom: '0.375rem' }}>Analyzing Resume...</h3>
                  <p style={{ fontSize: '0.8125rem', color: '#A16207' }}>AI is scanning for depth signals, ownership indicators, and red flags. This may take 15-30 seconds.</p>
                </Card>
              ) : (
                <div className="empty-state" style={{ paddingTop: '2rem' }}>
                  <p style={{ marginBottom: '1rem' }}>Click "Run Resume Intelligence" to analyze this candidate's resume for depth, ownership, and authenticity.</p>
                  {depthError && (
                    <div style={{ marginBottom: '1rem', padding: '0.625rem 1rem', borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA', fontSize: '0.8125rem', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 500, margin: '0 auto 1rem' }}>
                      <span>{depthError}</span>
                      <button onClick={() => setDepthError(null)} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontWeight: 700 }}>&times;</button>
                    </div>
                  )}
                  <Button onClick={runDepthAnalysis} disabled={analyzingDepth} style={{ background: '#D97706', color: '#fff', borderRadius: 10, fontWeight: 600 }}>
                    Run Resume Intelligence
                  </Button>
                </div>
              )}
            </div>
          ) : (() => {
            const da = depthAnalysis;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {/* Persistent result banner */}
                <Card style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#065F46' }}>Resume Intelligence Analysis Complete</span>
                  </div>
                  <Button variant="outline" onClick={runDepthAnalysis} disabled={analyzingDepth} style={{ borderRadius: 8, fontSize: '0.6875rem', padding: '0.25rem 0.625rem' }}>
                    {analyzingDepth ? 'Re-analyzing...' : 'Re-analyze'}
                  </Button>
                </Card>
                {/* Score cards */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                  {[
                    { label: 'Depth Score', val: da.depth_score, color: '#0076A3' },
                    { label: 'Ownership Score', val: da.ownership_score, color: '#0076A3' },
                    { label: 'Authenticity', val: da.authenticity_score, color: '#059669' },
                  ].map(s => (
                    <Card key={s.label} style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem', textAlign: 'center' }}>
                      <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '2rem', fontWeight: 700, color: s.color }}>{s.val || 0}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{s.label}</div>
                    </Card>
                  ))}
                </div>

                {/* Summary */}
                {da.summary && (
                  <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#002A38', marginBottom: '0.5rem' }}>AI Assessment</h4>
                    <p style={{ fontSize: '0.875rem', color: '#4A5568', lineHeight: 1.7 }}>{da.summary}</p>
                  </Card>
                )}

                {/* Shallow Flags */}
                {(da.shallow_flags || []).length > 0 && (
                  <Card style={{ background: '#fff', border: '1px solid #FDE68A', borderRadius: 14, padding: '1.25rem' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#D97706', marginBottom: '0.75rem' }}>Shallow Flags</h4>
                    {da.shallow_flags.map((f, i) => (
                      <div key={`sf-${i}`} style={{ padding: '0.5rem 0', borderBottom: i < da.shallow_flags.length - 1 ? '1px solid #FEF3C7' : 'none' }}>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#92400E' }}>{f.flag} <span style={{ fontSize: '0.625rem', color: '#D97706', fontWeight: 400 }}>({f.severity})</span></div>
                        <div style={{ fontSize: '0.75rem', color: '#78716C' }}>{f.evidence}</div>
                      </div>
                    ))}
                  </Card>
                )}

                {/* Ownership Signals */}
                {(da.ownership_signals || []).length > 0 && (
                  <Card style={{ background: '#fff', border: '1px solid #00A0E0', borderRadius: 14, padding: '1.25rem' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0076A3', marginBottom: '0.75rem' }}>Ownership Signals</h4>
                    {da.ownership_signals.map((s, i) => (
                      <div key={`os-${i}`} style={{ padding: '0.5rem 0', borderBottom: i < da.ownership_signals.length - 1 ? '1px solid #EDE9FE' : 'none' }}>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#5B21B6' }}>{s.signal} <span style={{ fontSize: '0.625rem', color: '#0076A3', fontWeight: 400 }}>({s.strength})</span></div>
                        <div style={{ fontSize: '0.75rem', color: '#78716C' }}>{s.evidence}</div>
                      </div>
                    ))}
                  </Card>
                )}

                {/* Red Flags */}
                {(da.red_flags || []).length > 0 && (
                  <Card style={{ background: '#fff', border: '1px solid #FECACA', borderRadius: 14, padding: '1.25rem' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#DC2626', marginBottom: '0.75rem' }}>Red Flags</h4>
                    {da.red_flags.map((r, i) => (
                      <div key={`rf-${i}`} style={{ padding: '0.5rem 0' }}>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#991B1B' }}>{r.flag} <span style={{ fontSize: '0.625rem', color: '#DC2626', fontWeight: 400 }}>({r.severity})</span></div>
                        <div style={{ fontSize: '0.75rem', color: '#78716C' }}>{r.details}</div>
                      </div>
                    ))}
                  </Card>
                )}

                {/* Follow-up Questions */}
                {(da.follow_up_questions || []).length > 0 && (
                  <Card style={{ background: '#fff', border: '1px solid #BBE3F5', borderRadius: 14, padding: '1.25rem' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0076A3', marginBottom: '0.75rem' }}>Suggested Follow-Up Questions</h4>
                    {da.follow_up_questions.map((q, i) => (
                      <div key={`fq-${i}`} style={{ padding: '0.625rem 0', borderBottom: i < da.follow_up_questions.length - 1 ? '1px solid #FFE7D9' : 'none' }}>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#002A38', marginBottom: '0.25rem' }}>"{q.question}"</div>
                        <div style={{ fontSize: '0.6875rem', color: '#64748B' }}>
                          <span style={{ color: '#0076A3' }}>{q.target_skill}</span> &middot; {q.probing_depth?.replace('_', ' ')}
                        </div>
                      </div>
                    ))}
                  </Card>
                )}
              </div>
            );
          })()}
        </TabsContent>
  );
}